/**
 * @foundation/cdn - Typed CDN asset URLs
 *
 * This package provides type-safe access to all assets available on the PMI CDN.
 * All assets are served from the production CDN at cdn.pm-international.com.
 *
 * @example
 * ```typescript
 * import { CDN, getCountryFlag, getFontStylesheet } from '@foundation/cdn'
 *
 * // Get a country flag URL
 * const flagUrl = getCountryFlag('US', '4x3')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/country-flags/4x3/us.svg'
 *
 * // Get a font stylesheet URL
 * const interFont = getFontStylesheet('inter')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/fonts/inter.css'
 * ```
 */

/**
 * Base CDN URL for all shared frontend assets
 */
export const CDN_BASE_URL =
  'https://cdn.pm-international.com/assets-shared-frontend' as const;

/**
 * CDN asset categories and their URLs
 */
export const CDN = {
  /**
   * Base URL for all assets
   */
  baseUrl: CDN_BASE_URL,

  /**
   * Country flag assets (from flag-icons v7.2.3)
   * Available in 1x1 (square) and 4x3 (rectangular) aspect ratios
   */
  flags: {
    /**
     * Base path for country flags
     */
    basePath: `${CDN_BASE_URL}/country-flags`,

    /**
     * Square flags (1:1 aspect ratio)
     */
    '1x1': `${CDN_BASE_URL}/country-flags/1x1`,

    /**
     * Rectangular flags (4:3 aspect ratio)
     */
    '4x3': `${CDN_BASE_URL}/country-flags/4x3`,
  },

  /**
   * Company logo assets
   */
  logos: {
    /**
     * Base path for company logos
     */
    basePath: `${CDN_BASE_URL}/company-logos`,

    /**
     * FitLine logo (SVG)
     */
    fitline: `${CDN_BASE_URL}/company-logos/fitline.svg`,

    /**
     * FitLine claims logo (SVG)
     */
    fitlineClaims: `${CDN_BASE_URL}/company-logos/fitline_claims.svg`,

    /**
     * PM International logo (SVG)
     */
    pm: `${CDN_BASE_URL}/company-logos/pm.svg`,

    /**
     * PM International logo (SVG)
     */
    pmClaims: `${CDN_BASE_URL}/company-logos/pm_claims.svg`,
  },

  /**
   * Font assets and stylesheets
   */
  fonts: {
    /**
     * Base path for fonts
     */
    basePath: `${CDN_BASE_URL}/fonts`,

    /**
     * FitLine custom font stylesheet
     */
    fitline: `${CDN_BASE_URL}/fonts/fitline.css`,

    /**
     * Inter font stylesheet
     */
    inter: `${CDN_BASE_URL}/fonts/inter.css`,

    /**
     * Uni Neue font stylesheet
     */
    uniNeue: `${CDN_BASE_URL}/fonts/uni-neue.css`,

    /**
     * Uni Neue font stylesheet (Nuvei variant)
     */
    uniNeueNuvei: `${CDN_BASE_URL}/fonts/uni-neue__nuvei.css`,

    /**
     * Wallop font stylesheet
     */
    wallop: `${CDN_BASE_URL}/fonts/wallop.css`,
  },

  fruits: {
    basePath: `${CDN_BASE_URL}/fruit-states`,
    addCard: CDN_BASE_URL + '/fruit-states' + '/add_card.png',
    cancelation: CDN_BASE_URL + '/fruit-states' + '/cancelation.png',
    emptyAbos: CDN_BASE_URL + '/fruit-states' + '/empty_abos.png',
    emptyBasket: CDN_BASE_URL + '/fruit-states' + '/empty_basket.png',
    fitLineClub: CDN_BASE_URL + '/fruit-states' + '/fitline_club.png',
    forbiden: CDN_BASE_URL + '/fruit-states' + '/forbiden.png',
    notFound: CDN_BASE_URL + '/fruit-states' + '/not_found.png',
    somethingWentWrong:
      CDN_BASE_URL + '/fruit-states' + '/something_went_wrong.png',
    success: CDN_BASE_URL + '/fruit-states' + '/success.png',
    underMaintenance: CDN_BASE_URL + '/fruit-states' + '/under_maintenance.png',
    whishlist: CDN_BASE_URL + '/fruit-states' + '/whishlist.png',
  },
} as const;

/**
 * Aspect ratio options for country flags
 */
export type FlagAspectRatio = '1x1' | '4x3';

/**
 * Available font stylesheet identifiers
 */
export type FontStylesheet =
  | 'fitline'
  | 'inter'
  | 'uniNeue'
  | 'uniNeueNuvei'
  | 'wallop';

/**
 * Available company logo identifiers
 */
export type CompanyLogo = 'fitline' | 'fitlineClaims' | 'pm';

/**
 * Get the CDN URL for a country flag
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (case-insensitive)
 * @param aspectRatio - Flag aspect ratio: '1x1' for square or '4x3' for rectangular
 * @returns Full CDN URL to the flag SVG file
 *
 * @example
 * ```typescript
 * getCountryFlag('US', '4x3')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/country-flags/4x3/us.svg'
 *
 * getCountryFlag('de', '1x1')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/country-flags/1x1/de.svg'
 * ```
 */
export function getCountryFlag(
  countryCode: string,
  aspectRatio: FlagAspectRatio = '4x3',
): string {
  return `${CDN.flags[aspectRatio]}/${countryCode.toLowerCase()}.svg`;
}

/**
 * Get the CDN URL for a font stylesheet
 *
 * @param font - Font identifier
 * @returns Full CDN URL to the font CSS file
 *
 * @example
 * ```typescript
 * getFontStylesheet('inter')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/fonts/inter.css'
 * ```
 */
export function getFontStylesheet(font: FontStylesheet): string {
  return CDN.fonts[font];
}

/**
 * Get the CDN URL for a company logo
 *
 * @param logo - Logo identifier
 * @returns Full CDN URL to the logo SVG file
 *
 * @example
 * ```typescript
 * getCompanyLogo('fitline')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/company-logos/fitline.svg'
 * ```
 */
export function getCompanyLogo(logo: CompanyLogo): string {
  return CDN.logos[logo];
}

/**
 * Build a custom CDN asset URL
 *
 * @param path - Relative path from the CDN base URL
 * @returns Full CDN URL
 *
 * @example
 * ```typescript
 * buildCdnUrl('images/banner.jpg')
 * // => 'https://cdn.pm-international.com/assets-shared-frontend/images/banner.jpg'
 * ```
 */
export function buildCdnUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${CDN_BASE_URL}/${cleanPath}`;
}
